<template>
  <div
    v-show="display"
    ref="root"
    tabindex="0"
    class="product-list"
    :style="{ top: `${elementPosition}px` }"
    @focusout="handleCloseProducts"
  >
    <div id="product-wrapper" class="product-list--wrapper">
      <v-progress-circular
        v-show="loading"
        class="product-list__progress-circular"
        :size="40"
        color="#FF4800"
        indeterminate
      ></v-progress-circular>
      <div :style="{ opacity: loading ? '0.5' : '1' }">
        <div>
          <h2>Produtos</h2>
          <div v-if="hasProductsData" class="product-list__box">
            <div class="content">
              <div
                v-for="(product, index) in userItems.productsData"
                :key="`${product.id}-${index}`"
                class="product"
                @click="redirectToLink(product.url)"
              >
                <circle-name
                  :subTitle="product.name"
                  :tooltip="{
                    disabled: false,
                    position: 'top',
                    contentClass: 'top',
                    span: product.name
                  }"
                  :label="product.name"
                  customClass="medium outline"
                  :isProducts="true"
                />
              </div>
            </div>
          </div>
          <div v-else class="product-list__empty">
            <p>Não há produtos contratados</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { getPosition } from '@/common/utils/getPositionElements'
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex'
import defaultMixins from '@/common/mixins'
import redirectToLink from '@/common/mixins/redirectToLink.js'

export default Vue.extend({
  props: {
    visible: Boolean
  },
  mixins: [defaultMixins, redirectToLink],
  components: {
    'circle-name': () =>
      import('../../simple-components/circle-name/CircleName.vue')
  },
  updated() {
    this.$refs.root?.focus()
  },
  data() {
    return {
      windowWidth: undefined,
      elementPosition: 0,
      hasProductsData: false,
      productListElementHeight: 0,
      loading: false,
      display: this.visibleProducts
    }
  },
  watch: {
    async visible(visible) {
      if (visible) {
        if (this.oidc && this.oidc.sub) {
          try {
            await this.getProductsData()
          } catch (error) {
          } finally {
            this.elementPosition = this.calculatePositionComponent()
          }
        }
      }
    },
    windowWidth() {
      this.elementPosition = this.calculatePositionComponent()
    },
    productListElementHeight() {
      this.elementPosition = this.calculatePositionComponent()
    },
    oidc() {
      if (this.oidc && this.oidc.sub) {
        this.getProductsData()
      }
    },
    currentEntitySelected() {
      this.getProductsData()
    },
    userItems() {
      this.hasProductsData =
        this.userItems &&
        this.userItems.productsData &&
        this.userItems.productsData.length > 0
      this.hasApplicationsData =
        this.userItems &&
        this.userItems.applicationsData &&
        this.userItems.applicationsData.length > 0
    },
    visibleProducts(visible) {
      this.display = false
      if (visible) {
        setTimeout(() => {
          this.display = true
        }, 0)
      }
    }
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth
    })

    const resizeObserver = new ResizeObserver(() => {
      this.productListElementHeight =
        document.getElementById('product-wrapper')?.clientHeight
    })

    resizeObserver.observe(document.getElementById('product-wrapper'))
  },
  computed: {
    ...mapState(['currentEntity', 'userItems', 'visibleProducts']),
    ...mapGetters({
      oidc: 'oidcUser'
    }),
    innerHeightScreen() {
      return window.innerHeight
    },
    currentEntitySelected() {
      return this.currentEntity.entitiesId
    }
  },

  methods: {
    ...mapMutations({
      setUserItems: 'SET_USER_ITEMS'
    }),
    ...mapActions({ updateVisibleProducts: 'setProductsVisibility' }),
    calculatePositionComponent() {
      const element = document.getElementById('apps-outline')
      const position = getPosition(element)?.y
      return position - this.productListElementHeight + 30
    },
    async getProductsData() {
      this.loading = true

      const entityID = this.currentEntity.entitiesId
        ? this.currentEntity.entitiesId
        : this.oidc.entity

      this.getProducts(entityID)

      this.loading = false
    },
    handleCloseProducts() {
      this.display = false
      this.updateVisibleProducts(false)
    }
  }
})
</script>

<style lang="scss">
.product-list {
  position: fixed;
  margin-left: 4rem;
  z-index: 10;
  height: 100%;
  outline: none;

  &__progress-circular {
    position: absolute !important;
    top: 45%;
    left: 45%;
  }

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 7px solid transparent;
    border-right: 10px solid $base-white;
    margin-top: -26px;
    margin-left: -10px;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 68px;

    p {
      color: $grayscale-lighten2;
      font-size: 0.75rem;
    }
  }

  .product-list--wrapper {
    box-shadow: $shadow-4;
    background: $base-white;
    border-radius: 8px;
    padding: 1.5rem 1.5rem;
    max-height: 355px;
    width: 275px;
    overflow-y: scroll;
    position: relative;
    h2 {
      color: $grayscale-lighten2;
      font-weight: 700;
      margin-bottom: 10px;
      font-size: $font-size-smaller;
      font-family: $font-default;
    }

    hr {
      margin-top: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .content {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 15px;

      .product {
        cursor: pointer;
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        width: 70px;

        h3 {
          font-size: $font-size-smaller;
          font-weight: 400;
          margin-top: 8px;
          margin-bottom: 0;
          max-width: 60px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 20px;
        }
      }
    }
  }
}

.top.v-tooltip__content::after {
  border-top: 9px solid transparent !important;
  border-bottom: 9px solid transparent !important;
  border-right: 9px solid #656872 !important;
  left: 46%;
  transform: rotate(270deg);
  margin-top: 25px !important;
}
</style>
